import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, DatePicker, Flex, Text } from "orcs-design-system";
import moment from "moment";
import PropTypes from "prop-types";
import { createGlobalStyle } from "styled-components";
import { API_DATE_FORMAT } from "src/consts/global";
import { ACTION_TYPES } from "src/components/OrgChart/context/actionTypes";
import { storeOrgChartDiffDates } from "src/components/OrgChart/utils/storeInLocalStorage";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";

const DatePickerContainer = createGlobalStyle`
  .DateRangePicker_picker {
    z-index: 10000 !important; 
  }
  .CalendarDay__hovered_span,
  .CalendarDay__selected_span {
    background: none;
    color: inherit;
    border: 1px solid #e4e7e7;
  }
`;

/**
 * Compares two moment dates to check if they represent the same day.
 * Handles null/undefined values correctly (both null = same, one null = different).
 * @param {moment.Moment|null|undefined} date1 - First date to compare.
 * @param {moment.Moment|null|undefined} date2 - Second date to compare.
 * @returns {boolean} True if both dates represent the same day, false otherwise.
 */
const isSameDay = (date1, date2) => {
  // if both dates are nullish, they are the same
  if (!date1 && !date2) {
    return true;
  }

  // if one is null and the other is not, they are different
  if (!date1 || !date2) {
    return false;
  }

  // if both dates are valid, compare them
  return date1.isSame(date2, "day");
};

/**
 * Date range picker component for selecting diff comparison dates in org chart.
 * Persists selected dates to workspace-specific localStorage and syncs with global state.
 * Handles focus management to show the correct month when clicking "From" or "To" fields.
 * @param {Object} props - Component props.
 * @param {Object} props.state - Org chart state containing viewOptions.
 * @param {Function} props.dispatch - Redux-style dispatch function for updating state.
 * @returns {React.ReactElement} The date picker component.
 */
export const DiffDatePicker = ({ state, dispatch }) => {
  const workspace = useWorkspace();
  const workspaceId = workspace?.id;
  const [focusedInput, setFocusedInput] = useState(null);
  const [dates, setDates] = useState({
    fromDate: state?.viewOptions?.fromDate || null,
    toDate: state?.viewOptions?.toDate || null,
  });

  const [today, minDate, maxDate] = useMemo(() => {
    return [
      moment(),
      moment().year(2000).startOf("year"), // what should the min be?
      moment().year(3000).endOf("year"), // Passing an empty value to the maxDate prop sets today as the default max date. This is a workaround.
    ];
  }, []);

  // Sync local dates state with viewOptions when props change (e.g., when navigating between teams)
  useEffect(() => {
    const stateFromDate = state?.viewOptions?.fromDate || null;
    const stateToDate = state?.viewOptions?.toDate || null;

    // Only update if state changed and local state differs
    // Use functional setState to avoid stale closure issues
    setDates((prevDates) => {
      if (
        isSameDay(prevDates.fromDate, stateFromDate) &&
        isSameDay(prevDates.toDate, stateToDate)
      ) {
        return prevDates; // No change needed
      }

      return {
        fromDate: stateFromDate,
        toDate: stateToDate,
      };
    });
  }, [state?.viewOptions?.fromDate, state?.viewOptions?.toDate]);

  const saveDates = useCallback(
    ({ fromDate, toDate }) => {
      // Only enable showChanges when both dates are valid
      // Changelog button will only show when both dates exist
      const showChanges = fromDate?.isValid() && toDate?.isValid();

      const validFromDate = fromDate?.isValid() ? fromDate : null;
      const validToDate = toDate?.isValid() ? toDate : null;

      // Persist dates to localStorage (workspace-specific)
      storeOrgChartDiffDates({
        fromDate: validFromDate,
        toDate: validToDate,
        workspaceId,
      });

      dispatch({
        type: ACTION_TYPES.UPDATE_VIEW_OPTIONS,
        payload: {
          ...state.viewOptions,
          showChanges,
          fromDate: validFromDate,
          toDate: validToDate,
        },
      });
    },
    [dispatch, state.viewOptions, workspaceId]
  );

  const setDatesSafe = useCallback(
    ({ startDate, endDate }) => {
      // skip if no changes
      if (
        isSameDay(dates.fromDate, startDate) &&
        isSameDay(dates.toDate, endDate)
      ) {
        return;
      }

      setDates({
        fromDate: startDate,
        toDate: endDate,
      });

      // Only save when both dates exist or both are cleared
      // This keeps it simple - changelog button only shows when both dates are populated
      if ((startDate && endDate) || (!startDate && !endDate)) {
        saveDates({ fromDate: startDate, toDate: endDate });
      }
    },
    [dates.fromDate, dates.toDate, saveDates]
  );

  // disable dates outside of the min/max range
  const isOutsideRange = useCallback(
    (date) => {
      return date.isBefore(minDate, "day") || date.isAfter(maxDate, "day");
    },
    [maxDate, minDate]
  );

  // highlight today (perhaps we can use this for something else?)
  const isDayHighlighted = useCallback(
    (date) => {
      return date.isSame(today, "day");
    },
    [today]
  );

  // Determine which month to show based on focused input
  // When clicking "To" field, show the month containing the "To" date
  // When clicking "From" field, show the month containing the "From" date
  const initialVisibleMonth = useCallback(() => {
    if (focusedInput === "endDate") {
      // Show the month containing the "To" date, or current month if not set
      return dates.toDate || today;
    }
    // Show the month containing the "From" date, or current month if not set
    return dates.fromDate || today;
  }, [focusedInput, dates.fromDate, dates.toDate, today]);

  return (
    <Box overflow="hidden">
      <Text>Compare changes</Text>
      <Flex mt="s" flexDirection="row">
        <Box zIndex={100000}>
          <DatePickerContainer />
          <DatePicker
            range
            small
            width="130px"
            withPortal
            keepFocusOnInput
            disableScroll
            showClearDates
            displayFormat={API_DATE_FORMAT}
            minDate={minDate}
            maxDate={maxDate}
            startDate={dates.fromDate}
            endDate={dates.toDate}
            startDatePlaceholderText="From"
            endDatePlaceholderText="To"
            focusedInput={focusedInput}
            initialVisibleMonth={initialVisibleMonth}
            onFocusChange={(focused) => {
              // Handle focus changes when user clicks on input fields
              // For range DatePickers, focused is passed directly: "startDate", "endDate", or null
              setFocusedInput(focused);
            }}
            onDatesChange={setDatesSafe}
            isOutsideRange={isOutsideRange}
            isDayHighlighted={isDayHighlighted}
            numberOfMonths={2}
            minimumNights={1}
            horizontalMargin={0}
          />
        </Box>
      </Flex>
    </Box>
  );
};

DiffDatePicker.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};
